import { getAuthSessionKey } from "./access";

export interface VoiceRequestOptions {
  orgId?: string;
  sessionKey?: string;
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
}

export interface VoiceApiError {
  message: string;
  status: number;
}

export async function voiceRequest<T>(path: string, options: VoiceRequestOptions = {}): Promise<T> {
  const response = await fetch(toVoicePath(path), {
    method: options.method || "GET",
    headers: buildHeaders(options),
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });

  const payload = await readPayload(response);
  if (!response.ok) {
    const message = extractErrorMessage(payload) || `Request failed with ${response.status}`;
    throw Object.assign(new Error(message), { status: response.status });
  }

  return payload as T;
}

export function extractList<T>(payload: unknown, key: string): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.data)) return record.data as T[];
  if (Array.isArray(record[key])) return record[key] as T[];
  return [];
}

export function formatDate(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

export function asText(value: unknown, fallback = "None"): string {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
}

function toVoicePath(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `/api/voice${normalized}`;
}

function buildHeaders(options: VoiceRequestOptions): Headers {
  const headers = new Headers({
    accept: "application/json"
  });

  if (options.body !== undefined) {
    headers.set("content-type", "application/json");
  }

  if (options.orgId) {
    headers.set("x-org-id", options.orgId);
  }

  const sessionKey = options.sessionKey ?? getAuthSessionKey();
  if (sessionKey) {
    headers.set("x-user-key", sessionKey);
  }

  return headers;
}

async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { text };
  }
}

function extractErrorMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  return typeof record.error === "string"
    ? record.error
    : typeof record.message === "string"
      ? record.message
      : null;
}
